<template>
  <div class="container">
    <div class="section process">
      <!-- Header -->
      <div class="content-header">
        <div>
          <h1 class="title is-3">Cultura - Gestión de Procesos</h1>
          <p class="subtitle is-6">
            Administra y supervisa todos los procesos de tu organización
          </p>
        </div>
        <img class="mytho-logo" src="@/assets/logo_Mytho.png" alt="Mytho" />
      </div>

      <!-- Filters and Actions -->
      <div class="columns is-vcentered filter-section">
        <div class="column search-column">
          <b-field>
            <b-input
              v-model="searchTerm"
              placeholder="Buscar procesos..."
              name="search"
              type="search"
              icon="magnify"
              @input="onQuickFilterChanged"
            ></b-input>
          </b-field>
        </div>

        <div class="column"></div>

        <div class="column is-narrow">
          <b-dropdown
            v-model="statusFilter"
            aria-role="list"
            class="status-filter-dropdown"
          >
            <template #trigger="{ active }">
              <b-button
                :icon-right="active ? 'menu-up' : 'menu-down'"
                class="status-filter-trigger"
              >
                <b-icon
                  icon="filter-variant"
                  size="is-small"
                  class="filter-icon"
                ></b-icon>
                <span class="filter-label">Estado:</span>
                <span v-if="statusFilter === 'all'" class="has-text-grey">
                  Todos
                </span>
                <span
                  v-else
                  class="tag"
                  :class="statusConfig[statusFilter].class"
                >
                  <i
                    class="icon-badge mdi"
                    :class="`mdi-${statusConfig[statusFilter].icon}`"
                  ></i>
                  {{ statusConfig[statusFilter].label }}
                </span>
              </b-button>
            </template>

            <b-dropdown-item value="all" aria-role="listitem">
              <span class="has-text-grey">Todos los estados</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-for="(cfg, key) in statusConfig"
              :key="key"
              :value="key"
              aria-role="listitem"
            >
              <span class="tag" :class="cfg.class">
                <i class="icon-badge mdi" :class="`mdi-${cfg.icon}`"></i>
                {{ cfg.label }}
              </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <div v-if="tokenUser && tokenUser.admin" class="column is-narrow">
          <b-button
            type="is-dark"
            class="button-create"
            @click="isCreateModalActive = true"
          >
            <b-icon icon="plus" class="mr-2"></b-icon>
            Nuevo Proceso
          </b-button>
        </div>
      </div>

      <!-- AG Grid Table -->
      <div class="box ag-grid-container">
        <ag-grid-vue
          id="table-process-c"
          style="width: 100%; height: 500px;"
          class="ag-theme-material"
          :gridOptions="gridOptions"
          :columnDefs="columnDefs"
          :rowData="filteredProcesses"
          :defaultColDef="defaultColDef"
          :domLayout="'normal'"
          :rowSelection="'single'"
          :suppressCellFocus="true"
          :suppressRowClickSelection="false"
          :isFullWidthRow="isFullWidthRow"
          :fullWidthCellRenderer="fullWidthCellRenderer"
          @grid-ready="onGridReady"
          @row-clicked="onRowClicked"
        >
        </ag-grid-vue>
        <div class="row-count-footer">
          {{ filteredProcesses.length }}
          {{ filteredProcesses.length === 1 ? "proceso" : "procesos" }}
        </div>
      </div>
    </div>

    <!-- Create Process Modal -->
    <create-process-modal
      v-model="isCreateModalActive"
      :editForm="editForm"
      @handleCreateProcess="handleCreateProcess"
      @handleEditProcess="handleEditProcess"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
// Components
import { AgGridVue } from "ag-grid-vue";
import ActionsProcess from "../components/Process/ActionsProcess.vue";
import CreateProcessModal from "../components/Process/CreateProcessModal.vue";

// Store
import store from "../store";
import {
  createProcess,
  deleteProcess,
  fetchProcesses,
  updateProcess,
} from "../api";
import { formatDate, hasPermission, statusConfig } from "../helpers";

export default {
  name: "ProcessList",
  components: {
    AgGridVue,
    CreateProcessModal,
    /* eslint-disable-next-line vue/no-unused-components */
    actionsProcess: ActionsProcess,
  },
  data() {
    return {
      // Modal
      isCreateModalActive: false,
      editForm: {},
      // Filters
      searchTerm: "",
      statusFilter: "all",
      // Table
      gridApi: null,
      gridColumnApi: null,
      isFullWidthRow: null,
      fullWidthCellRenderer: null,
      gridOptions: {
        context: {
          componentParent: this,
        },
        tooltipShowDelay: 300,
        overlayNoRowsTemplate: `
          <div class="custom-no-rows">
            <p style="color: #7a7a7a">No hay procesos</p>
          </div>
        `,
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        autoHeight: true,
        resizable: false,
        suppressMovable: true,
        suppressMenu: true,
        tooltipValueGetter: (params) =>
          params.data ? `Ir al proceso ${params.data.name}` : "",
      },
    };
  },
  computed: {
    ...mapState(["isMovil", "idtoken", "tokenUser"]),
    statusConfig() {
      return statusConfig;
    },
    processes() {
      return store.state.processes;
    },
    filteredProcesses() {
      if (this.statusFilter === "all") return this.processes;
      return this.processes.filter((p) => p.status === this.statusFilter);
    },
    columnDefs() {
      return [
        {
          headerName: "Nombre",
          field: "name",
          flex: 2,
          cellRenderer: (params) => {
            return `
              <div style="display: flex; align-items: center; gap: 0.75rem;">
                ${this.formatTypeProcess(params)}
                <div>
                 ${params.data.name}
                </div>
              </div>
            `;
          },
        },
        {
          headerName: "Estado",
          field: "status",
          flex: 1,
          cellStyle: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          cellRenderer: (params) => {
            return this.createStatusBadge(params.data);
          },
        },
        {
          headerName: "Progreso",
          field: "percentage",
          flex: 1,
          cellRenderer: (params) => {
            const progress = params.value.toFixed(0) || 0;
            return `
              <div style="display: flex; align-items: center; gap: 0.55rem; height: 100%;">
                <div style="flex: 1; background-color: #f5f5f5; border-radius: 9999px; height: 8px;">
                  <div style="background-color: #61D261; height: 8px; border-radius: 9999px; width: ${progress}%; transition: width 0.3s;"></div>
                </div>
                <span style="font-size: 0.813rem; color: #7a7a7a; min-width: 2rem; text-align: right;">${progress}%</span>
              </div>
            `;
          },
        },
        {
          headerName: "Creado",
          field: "evaluationDate",
          flex: 1,
          cellStyle: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          valueFormatter: (params) => {
            return formatDate(params.value);
          },
        },
        {
          headerName: "",
          field: "_id",
          flex: 0.5,
          cellRenderer: "actionsProcess",
          cellStyle: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          onCellClicked: (params) => {
            params.api.deselectAll();
          },
          suppressNavigable: true,
          sortable: false,
          hide: !hasPermission(this.tokenUser, ["admin"]),
        },
        {
          headerName: "",
          colId: "chevron",
          width: 56,
          sortable: false,
          suppressNavigable: true,
          cellStyle: {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            paddingRight: "1rem",
          },
          cellRenderer: () => {
            return `<i class="mdi mdi-chevron-right chevron-cell"></i>`;
          },
        },
      ];
    },
  },
  created() {
    this.isFullWidthRow = this.isFullWidthRowFunc.bind(this);
    this.fullWidthCellRenderer = this.fullWidthCellRendererFunc.bind(this);

    if (this.idtoken) {
      this.getProcesses();
    }
  },
  methods: {
    // API
    async getProcesses() {
      await fetchProcesses()
        .then((res) => store.dispatch("setProcesses", res))
        .catch((err) => console.log(err));
    },
    // Table
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
    },
    isFullWidthRowFunc(params) {
      return false;
    },
    fullWidthCellRendererFunc(params) {
      const process = params.data;
      const config = statusConfig[process.status];

      return `
        <div class="process-card-mobile" data-id="${process.id}">
          <!-- Card Header -->
          <div class="card-header-mobile">
            <div class="card-title-section-mobile">
              <div class="process-title-mobile">
                <span class="icon has-text-${config.class.replace("is-", "")}">
                  <i class="mdi mdi-${config.icon}"></i>
                </span>
                <strong>${process.name}</strong>
              </div>
              <p class="process-description-mobile">${process.description}</p>
            </div>
            <div class="card-actions-mobile">
              <span class="tag ${config.class}">${config.label}</span>
              <button class="button is-small action-button-mobile" data-id="${
                process.id
              }">
                <span class="icon is-small">
                  <i class="mdi mdi-dots-horizontal"></i>
                </span>
              </button>
            </div>
          </div>

          <!-- Progress -->
          <div class="progress-section-mobile">
            <div class="progress-header-mobile">
              <span class="is-size-7">Progreso</span>
              <span class="is-size-7">${process.progress}%</span>
            </div>
            <progress class="progress is-primary" value="${
              process.progress
            }" max="100">${process.progress}%</progress>
          </div>

          <!-- Footer -->
          <div class="card-footer-mobile">
            <div class="collaborators-mobile">
              <span class="icon is-small">
                <i class="mdi mdi-account-multiple"></i>
              </span>
              <div class="avatars-mobile">
                0
              </div>
            </div>
            <span class="is-size-7 has-text-grey">${formatDate(
              process.evaluationDate,
            )}</span>
          </div>
        </div>
      `;
    },
    onRowClicked(event) {
      if (event.event.target.closest(".action-button")) return;
      if (event.data.status !== "completed" && !this.tokenUser.admin) {
        return this.$store.dispatch("notify", {
          message: "Este proceso aún no ha finalizado.",
          type: "is-warning",
        });
      }
      this.onSelectProcess(event.data);
    },
    onQuickFilterChanged() {
      if (this.gridApi) {
        this.gridApi.setQuickFilter(this.searchTerm);
      }
    },
onSelectProcess(process) {
      store.dispatch("selectProcess", process);
      this.$router.push({ name: "culturaProcessDetail" });
    },
    // Components
    formatTypeProcess(p) {
      if (!p.data.benchmark) return "";
      return `<span class="tag tag-process is-success is-capitalized"><i class="mdi mdi-database-import-outline"></i></span>`;
    },
    createStatusBadge(data) {
      const statusTypes = {
        ...statusConfig,
        default: { label: "Sin estado", class: "is-light", icon: "moon-new" },
      };
      const config = statusTypes[data.status] || statusTypes.default;
      return `<span class="tag ${config.class}"><i class="icon-badge mdi mdi-${config.icon}"></i>${config.label}</span>`;
    },
    // Actions
    async handleCreateProcess(form) {
      await createProcess(form)
        .then(async (res) => {
          await this.getProcesses().finally(() => {
            this.isCreateModalActive = false;
            this.$store.dispatch("notify", {
              message: "Proceso creado correctamente.",
              type: "is-success",
            });
          });
        })
        .catch((err) => {
          console.log(err);
          this.$store.dispatch("notify", {
            message: "Error al crear el proceso.",
            type: "is-danger",
          });
        });
    },
    async handleEditProcess(form) {
      await updateProcess(form)
        .then(async (res) => {
          await this.getProcesses().finally(() => {
            this.isCreateModalActive = false;
            this.$store.dispatch("notify", {
              message: "Proceso actualizado correctamente.",
              type: "is-success",
            });
          });
        })
        .catch((err) => {
          console.log(err);
          this.$store.dispatch("notify", {
            message: "Error al actualizar el proceso.",
            type: "is-danger",
          });
        });
    },
    async handleDelete(id) {
      const config = {
        title: "¿Estás seguro de eliminar este registro?",
        text:
          "Se eliminará toda la información relacionada y no podrás recuperarla después.",
        icon: "warning",
        showCancelButton: true,
        cancelButtonColor: "#de5b5b",
        confirmButtonText: "Sí, eliminar",
      };
      await this.$swal(config).then(async (result) => {
        if (result.isConfirmed) {
          await deleteProcess(id)
            .then(async (res) => {
              await this.getProcesses().finally(() => {
                this.$store.dispatch("notify", {
                  message: "Proceso eliminado correctamente.",
                  type: "is-success",
                });
              });
            })
            .catch((err) => {
              console.log(err);
              this.$store.dispatch("notify", {
                message: "Error al eliminar el proceso.",
                type: "is-danger",
              });
            });
        }
      });
    },
    handleShowEdit({ _id, name, evaluationDate, test }) {
      this.editForm = { _id, name, evaluationDate, test };
      this.isCreateModalActive = true;
    },
    // Helper
    hasPermission,
  },
  watch: {
    idtoken(nv) {
      if (nv) {
        this.getProcesses();
      }
    },
    isCreateModalActive(nv) {
      if (!nv) this.editForm = {};
    },
  },
};
</script>

<style>
@import "~ag-grid-community/styles/ag-grid.css";
@import "~ag-grid-community/styles/ag-theme-material.css";

.ag-root-wrapper {
  border-radius: 10px !important;
}

#table-process-c .ag-row-selected::before {
  background-color: unset !important;
}

/* Make rows read as clickable (navigates to process detail) */
#table-process-c .ag-row {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

#table-process-c .ag-row:hover {
  background-color: #f7f4fb;
}

#table-process-c .chevron-cell {
  font-size: 1.35rem;
  line-height: 1;
  color: #cfcfcf;
  transition: color 0.15s ease, transform 0.15s ease;
}

#table-process-c .ag-row:hover .chevron-cell {
  color: #510087;
  transform: translateX(3px);
}

/* Cultura tooltip theme: dark, rounded, bordered */
#table-process-c .ag-tooltip {
  background-color: #2b2b2b;
  color: #fff;
  border: 1px solid #6b2fae;
  border-radius: 8px;
  padding: 0.4rem 0.7rem;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
}

.button-create > span {
  display: flex;
}

@media (min-width: 1023px) {
  .datepicker-in-modal .dropdown-content {
    transform: scale(0.8);
    transform-origin: top left;
  }
}
.datepicker-in-modal
  .dropdown.is-mobile-modal:not(.is-hoverable)
  > .dropdown-menu {
  z-index: 1000 !important;
}

.datepicker-in-modal .dropdown-menu {
  z-index: 1000 !important;
}

.form-processes-c .label {
  font-weight: 400 !important;
}

.action-button {
  border-width: 0px !important;
  border-radius: 10px;
}

.process .icon-badge {
  margin-right: 5px;
  height: auto !important;
  font-size: 17px !important;
}

.process .tag-process {
  margin-right: 5px;
  height: auto !important;
  font-size: 17px !important;
  padding-left: 0.35em !important;
  padding-right: 0.35em !important;
}

.status-filter-dropdown .dropdown-item {
  padding: 0.5rem 2rem 0.5rem 2rem;
  position: relative;
  background-color: transparent !important;
  color: inherit !important;
}

.status-filter-dropdown .dropdown-item:hover {
  background-color: #f5f5f5 !important;
}

.status-filter-dropdown .dropdown-item.is-active::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background-color: #7957d5;
}

.status-filter-dropdown .dropdown-item.is-active::after {
  content: "\F012C";
  font-family: "Material Design Icons";
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #7957d5;
  font-size: 16px;
}

.status-filter-dropdown .icon-badge {
  margin-right: 5px;
  font-size: 17px;
}

.status-filter-trigger {
  min-width: 220px;
  justify-content: flex-start;
  gap: 0.4rem;
}

.status-filter-trigger .filter-icon {
  color: #7a7a7a;
}

.status-filter-trigger .filter-label {
  color: #7a7a7a;
  font-weight: 500;
  margin-right: 0.25rem;
}
</style>

<style scoped>
.content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 2rem;
}

.content-header .mytho-logo {
  height: 44px;
  width: auto;
  object-fit: contain;
  flex: none;
}

.filter-section {
  margin-bottom: 1.5rem;
}

.search-column {
  max-width: 360px;
}

.ag-grid-container {
  padding: 0;
}

.row-count-footer {
  padding: 0.65rem 1rem;
  border-top: 1px solid #f0f0f0;
  font-size: 0.813rem;
  color: #7a7a7a;
  text-align: right;
}

.status-tag {
  cursor: pointer;
  transition: opacity 0.2s;
}

.status-tag:hover {
  opacity: 0.8;
}

.collaborators {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.avatars {
  display: flex;
  align-items: center;
}

.avatar {
  margin-left: -8px;
  border: 2px solid white;
  border-radius: 50%;
}

.avatar:first-child {
  margin-left: 0;
}

.avatar-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: -8px;
  border-radius: 50%;
  background-color: #f5f5f5;
  border: 2px solid white;
  font-size: 0.7rem;
  color: #7a7a7a;
}

/* Mobile Cards */
.process-card {
  cursor: pointer;
  transition: background-color 0.2s;
  margin-bottom: 1rem;
}

.process-card:hover {
  background-color: #fafafa;
}

.card-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.card-title-section {
  flex: 1;
  min-width: 0;
}

.process-title-mobile {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.process-description-mobile {
  font-size: 0.875rem;
  color: #7a7a7a;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  margin-left: 1rem;
  flex-shrink: 0;
}

.progress-section {
  margin-bottom: 1rem;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  color: #7a7a7a;
}

.progress-section .progress {
  margin-bottom: 0;
}

.card-footer-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
}

.dropdown-container-temp .dropdown-menu {
  min-width: 7rem !important;
}

.dropdown-container-temp .dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  cursor: pointer;
}

.dropdown-container-temp .dropdown-item:hover {
  background-color: #f5f5f5;
}
</style>



// WEBPACK FOOTER //
// src/components/cultura/views/Processes.vue